import { formatGqlError, formatGqlErrors } from 'common/GqlUtilities';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router';
import imgUnavailable from 'src/assets/img/img_unavailable.jpg';
import { DishDescriptionFoodItemFragment, useAdminModifyFoodItemMutation } from 'src/gqlReactTypings.generated.d';
import { FoodItemWithOptionalQuantity } from 'src/pages/consumer/order/components/ConsumerOrderDishSelection';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import Notifications from 'src/shared/Notifications';
import { Routes } from 'src/shared/Routes';
import { getIdSluggedPath } from 'src/shared/utils/RouteUtilities';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { DishAvailabilityStatus } from '../AddToCartButton';
import { DishDescription } from '../DishDescription';
import { UnavailableOverlay } from '../UnavailableOverlay';
import { DishInfo, DynamicImg, ImgContainer, PositionedDishTags, PositionedQuantityRemainingTag } from './styles';

interface DishInfoSectionProps {
  onClick: () => void;
  showDishTags: boolean;
  availabilityStatus: DishAvailabilityStatus;
  isUnavailable: boolean;
  foodItem: FoodItemWithOptionalQuantity & DishDescriptionFoodItemFragment;
}

export const DishInfoSection: React.FC<DishInfoSectionProps> = ({
  onClick,
  showDishTags,
  availabilityStatus,
  isUnavailable,
  foodItem,
}) => {
  const history = useHistory();
  const [currentUser] = useCurrentUser();
  const { isSmallScreen } = useScreenContext();

  const isPopular = Boolean(foodItem.isPopular && showDishTags);
  const useBigImages = isSmallScreen && (isPopular || !currentUser?.orderCount);
  // See experiment results for big images: https://www.notion.so/Test-Debriefs-Meeting-Notes-ca660ef523d842c08c47e3eb7475424d
  const imageHeight = isSmallScreen && !useBigImages ? 100 : 184;
  const imageWidth = isSmallScreen ? (useBigImages ? '100%' : 100) : '100%';

  const [adminModifyFoodItemMutation] = useAdminModifyFoodItemMutation();

  const handelAdminEditClick = useCallback(() => {
    const variables = { foodItemId: foodItem.id, changeset: {}, autoApproveIfPossible: false };
    adminModifyFoodItemMutation({ variables })
      .then(({ data, errors }) => {
        if (errors) return Notifications.error(formatGqlErrors(errors));
        if (!data) return;
        const path = getIdSluggedPath(Routes.ADMIN_CHANGE_REQUEST, data.createFoodItemChangeRequest.id.toString());
        history.push(path);
      })
      .catch((err) => Notifications.error(formatGqlError(err)));
  }, [adminModifyFoodItemMutation, foodItem.id, history]);

  return (
    <DishInfo onClick={onClick} isBigImage={useBigImages}>
      <ImgContainer isBigImage={useBigImages}>
        {showDishTags && <PositionedDishTags isPopular={isPopular} />}
        <PositionedQuantityRemainingTag foodItem={foodItem} />
        {availabilityStatus !== DishAvailabilityStatus.AVAILABLE && (
          <UnavailableOverlay onClick={onClick}>{isUnavailable ? 'Unavailable' : 'Sold Out'}</UnavailableOverlay>
        )}
        {foodItem.imageUrl ? (
          <DynamicImg
            height={imageHeight}
            width={imageWidth}
            sourceHeight={imageHeight}
            src={foodItem.imageUrl}
            alt='Food'
          />
        ) : (
          <img
            style={{ objectFit: 'cover', width: imageWidth, height: imageHeight }}
            src={imgUnavailable}
            alt='Unavailable'
          />
        )}
      </ImgContainer>
      <DishDescription
        isBigImage={useBigImages}
        showSoldOutOverlay={availabilityStatus !== DishAvailabilityStatus.AVAILABLE}
        onSoldOutOverlayClick={onClick}
        showAdminEditButton={Boolean(currentUser?.admin)}
        onAdminEditClick={handelAdminEditClick}
        foodItem={foodItem}
      />
    </DishInfo>
  );
};
